import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppShell from '../components/AppShell';
import HazardModule from '../components/HazardModule';
import {
  reportsApi,
  type AnalyzeLocationPayload,
  type AnalyzeLocationResponse,
  type ReportScoreMap,
  type ReportSummaryCard,
  type SavedReport,
  type SavedReportDetail,
  type SupportedCountry
} from '../services/api';

type GoogleAddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

type GoogleAutocomplete = {
  getPlace(): {
    name?: string;
    formatted_address?: string;
    geometry?: { location?: { lat(): number; lng(): number } };
    place_id?: string;
    address_components?: GoogleAddressComponent[];
  };
  addListener(eventName: 'place_changed', handler: () => void): void;
};

type GoogleAutocompleteOptions = {
  fields: string[];
  componentRestrictions?: {
    country: string[] | string;
  };
};

type GoogleMaps = {
  maps: {
    places: {
      Autocomplete: new (input: HTMLInputElement, options: GoogleAutocompleteOptions) => GoogleAutocomplete;
    };
    event: {
      clearInstanceListeners(instance: unknown): void;
    };
  };
};

type SelectedLocation = {
  name: string;
  formatted_address?: string;
  latitude: number;
  longitude: number;
  place_id?: string;
  country_code?: string;
  country?: string;
  address_components?: GoogleAddressComponent[];
};

const getComponentValue = (components: GoogleAddressComponent[] | undefined, type: string) =>
  components?.find((component) => component.types?.includes(type))?.long_name ?? null;

const getComponentShortValue = (components: GoogleAddressComponent[] | undefined, type: string) =>
  components?.find((component) => component.types?.includes(type))?.short_name ?? null;

const buildDisplayLabel = (components: GoogleAddressComponent[] | undefined, fallback: string) => {
  const streetNumber = getComponentValue(components, 'street_number');
  const route = getComponentValue(components, 'route');
  const locality = getComponentValue(components, 'locality') ?? getComponentValue(components, 'postal_town');
  const state = getComponentValue(components, 'administrative_area_level_1');
  const postalCode = getComponentValue(components, 'postal_code');
  const country = getComponentValue(components, 'country');

  const segments: string[] = [];
  if (route) {
    segments.push([route, streetNumber].filter(Boolean).join(' ').trim());
  } else if (locality) {
    segments.push(locality);
  }
  if (postalCode) {
    segments.push(postalCode);
  }
  if (locality && segments[0] !== locality) {
    segments.push(locality);
  }
  if (state && state !== locality) {
    segments.push(state);
  }
  if (country) {
    segments.push(country);
  }
  const built = segments.filter(Boolean).join(', ');
  return built || fallback;
};

const normaliseFormattedAddress = (
  raw: string | undefined,
  fallback: string,
  components: GoogleAddressComponent[] | undefined
) => {
  if (!raw) return fallback;
  const hasRoute = Boolean(getComponentValue(components, 'route'));
  if (hasRoute) return raw;
  const trimmed = raw.trim();
  if (/^\d+\s*$/u.test(trimmed)) {
    return fallback;
  }
  if (/^\d+\s*,/u.test(trimmed)) {
    return fallback;
  }
  return raw;
};

const SCORE_CONFIG: { key: keyof ReportScoreMap; label: string }[] = [
  { key: 'overall', label: 'Composite' },
  { key: 'energy', label: 'Energy' },
  { key: 'fibre', label: 'Fibre' },
  { key: 'data_center', label: 'DC Connectivity' }
];

const SUMMARY_RADIUS_LABELS: Record<string, string> = {
  'Power Plants': '≤ 10 km',
  'Substations': '≤ 10 km',
  'Data Centers': '≤ 100 km',
  'Industrial Sites': '≤ 10 km',
  'Fiber Assets': '≤ 10 km',
  'Municipalities': '≤ 10 km'
};

const GENERATION_STEPS = [
  'Loading infrastructure datasets',
  'Computing grid distances',
  'Building regional intelligence',
  'Rendering map, charts and HTML'
] as const;
const SHOW_HAZARD_SECTION = false;

const HISTORY_PAGE_SIZE = 20;
const HISTORY_RETRY_ATTEMPTS = 3;
const HISTORY_RETRY_DELAY_MS = 900;
const REPORT_FETCH_ATTEMPTS = 6;
const REPORT_FETCH_DELAY_MS = 1000;
const FALLBACK_SUPPORTED_COUNTRIES: SupportedCountry[] = [
  { code: 'AT', slug: 'at', name: 'Austria', active: true, rollout_status: 'active' },
  { code: 'BE', slug: 'be', name: 'Belgium', active: true, rollout_status: 'active' },
  { code: 'BG', slug: 'bg', name: 'Bulgaria', active: true, rollout_status: 'active' },
  { code: 'HR', slug: 'hr', name: 'Croatia', active: true, rollout_status: 'active' },
  { code: 'CY', slug: 'cy', name: 'Cyprus', active: true, rollout_status: 'active' },
  { code: 'CZ', slug: 'cz', name: 'Czechia', active: true, rollout_status: 'active' },
  { code: 'DK', slug: 'dk', name: 'Denmark', active: true, rollout_status: 'active' },
  { code: 'EE', slug: 'ee', name: 'Estonia', active: true, rollout_status: 'active' },
  { code: 'FI', slug: 'fi', name: 'Finland', active: true, rollout_status: 'active' },
  { code: 'FR', slug: 'fr', name: 'France', active: true, rollout_status: 'active' },
  { code: 'DE', slug: 'de', name: 'Germany', active: true, rollout_status: 'active' },
  { code: 'GR', slug: 'gr', name: 'Greece', active: true, rollout_status: 'active' },
  { code: 'HU', slug: 'hu', name: 'Hungary', active: true, rollout_status: 'active' },
  { code: 'IE', slug: 'ie', name: 'Ireland', active: true, rollout_status: 'active' },
  { code: 'IT', slug: 'it', name: 'Italy', active: true, rollout_status: 'active' },
  { code: 'LV', slug: 'lv', name: 'Latvia', active: true, rollout_status: 'active' },
  { code: 'LT', slug: 'lt', name: 'Lithuania', active: true, rollout_status: 'active' },
  { code: 'LU', slug: 'lu', name: 'Luxembourg', active: true, rollout_status: 'active' },
  { code: 'MT', slug: 'mt', name: 'Malta', active: true, rollout_status: 'active' },
  { code: 'NL', slug: 'nl', name: 'Netherlands', active: true, rollout_status: 'active' },
  { code: 'PL', slug: 'pl', name: 'Poland', active: true, rollout_status: 'active' },
  { code: 'PT', slug: 'pt', name: 'Portugal', active: true, rollout_status: 'active' },
  { code: 'RO', slug: 'ro', name: 'Romania', active: true, rollout_status: 'active' },
  { code: 'SK', slug: 'sk', name: 'Slovakia', active: true, rollout_status: 'active' },
  { code: 'SI', slug: 'si', name: 'Slovenia', active: true, rollout_status: 'active' },
  { code: 'ES', slug: 'es', name: 'Spain', active: true, rollout_status: 'active' },
  { code: 'SE', slug: 'se', name: 'Sweden', active: true, rollout_status: 'active' },
  { code: 'GB', slug: 'gb', name: 'United Kingdom', active: true, rollout_status: 'active' },
  { code: 'NO', slug: 'no', name: 'Norway', active: true, rollout_status: 'active' },
  { code: 'CH', slug: 'ch', name: 'Switzerland', active: true, rollout_status: 'active' }
];

const dedupeReports = (items: SavedReport[]): SavedReport[] => {
  const seen = new Set<string>();
  const result: SavedReport[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    result.push(item);
  }
  return result;
};

const SCORE_DESCRIPTIONS: Record<string, string> = {
  overall: 'Composite of energy, fibre, data center and location readiness.',
  energy: 'Balances dispatchable generation potential with HV substation density in the near field.',
  fibre: 'Rewards dense fibre-fed municipalities and resilient PoP coverage around the site.',
  data_center: 'Highlights proximity-weighted density of surrounding data centers, with extra weight on hyperscale footprints.',
  location: 'Tracks surrounding population depth, municipal variety, and metro reach for contextual strength.'
};

const sleep = (ms: number) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

const getScoreValue = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  return Math.max(0, Math.min(10, num));
};

const scoreColor = (value: number | null) => {
  if (value === null) return 'bg-white/10';
  if (value >= 8) return 'bg-[#22c55e]';
  if (value >= 5) return 'bg-[#fbbf24]';
  return 'bg-[#ef4444]';
};

const formatRadiusLabel = (card: ReportSummaryCard): string | null => {
  if (typeof card.display_radius_label === 'string') {
    const normalizedLabel = card.display_radius_label.trim();
    if (normalizedLabel.length > 0) {
      return normalizedLabel;
    }
  }
  if (card.label && SUMMARY_RADIUS_LABELS[card.label]) {
    return SUMMARY_RADIUS_LABELS[card.label];
  }
  const radius = card.display_radius_km ?? card.radius_km ?? card.range_km;
  if (radius === null || radius === undefined) {
    return null;
  }
  if (typeof radius === 'string') {
    return radius;
  }
  if (typeof radius === 'number') {
    const fixedValue = radius >= 10 ? radius.toFixed(0) : radius.toFixed(1);
    return `≤ ${fixedValue} km`;
  }
  return null;
};

const ScoreBar = ({ label, value, tooltip }: { label: string; value: number | null; tooltip?: string }) => {
  const filledSegments = value !== null ? Math.max(0, Math.min(10, Math.floor(value))) : 0;
  const colorClass = scoreColor(value);

  return (
    <div className="group relative rounded-2xl border border-black/10 bg-[#f9f9f9] p-4 shadow-[0_12px_30px_rgba(0,0,0,0.08)]">
      <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-[#8a8b99]">
        <span className="font-semibold text-[#0f0f19]">{label}</span>
        <span className="text-sm font-semibold text-[#0f0f19]">{value !== null ? value.toFixed(1) : 'n/a'}</span>
      </div>
      <div className="mt-3 flex gap-1">
        {Array.from({ length: 10 }).map((_, index) => (
          <span
            key={`${label}-${String(index)}`}
            className={`h-2 flex-1 rounded-sm ${index < filledSegments ? colorClass : 'bg-white/10'}`}
          />
        ))}
      </div>
      {tooltip && (
        <div className="pointer-events-none absolute left-1/2 top-0 z-10 hidden w-64 -translate-x-1/2 -translate-y-full rounded-2xl border border-white/10 bg-black/80 px-4 py-3 text-[11px] text-slate-200 shadow-[0_18px_45px_rgba(3,2,19,0.6)] group-hover:block">
          {tooltip}
        </div>
      )}
    </div>
  );
};

declare global {
  interface Window {
    google: GoogleMaps | undefined;
  }
}

const loadGooglePlaces = (() => {
  let loader: Promise<GoogleMaps | undefined> | null = null;
  let cachedKey: string | null = null;
  return (apiKey: string) => {
    if (window.google?.maps?.places) {
      return Promise.resolve(window.google);
    }
    if (!apiKey) {
      return Promise.reject(new Error('Missing Google Maps API key'));
    }
    if (!loader || cachedKey !== apiKey) {
      cachedKey = apiKey;
      loader = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.google);
        script.onerror = () => reject(new Error('Failed to load Google Maps API'));
        document.head.appendChild(script);
      });
    }
    return loader;
  };
})();

const INITIAL_MAPS_KEY = typeof import.meta.env.VITE_GOOGLE_MAPS_API_KEY === 'string' ? import.meta.env.VITE_GOOGLE_MAPS_API_KEY : null;

const ReportsPage = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<GoogleAutocomplete | null>(null);
  const completionTimeoutRef = useRef<number | null>(null);

  const [searchValue, setSearchValue] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualLatitude, setManualLatitude] = useState('');
  const [manualLongitude, setManualLongitude] = useState('');
  const [manualLabel, setManualLabel] = useState('');
  const [manualCountryCode, setManualCountryCode] = useState('DE');

  const [reports, setReports] = useState<SavedReport[]>([]);
  const [totalReportsCount, setTotalReportsCount] = useState<number | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [currentReport, setCurrentReport] = useState<SavedReportDetail | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [deepStatus, setDeepStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [deepHtml, setDeepHtml] = useState<string | null>(null);
  const [deepError, setDeepError] = useState<string | null>(null);

  const [mapsApiKey, setMapsApiKey] = useState<string | null>(INITIAL_MAPS_KEY);
  const [supportedCountries, setSupportedCountries] = useState<SupportedCountry[]>(FALLBACK_SUPPORTED_COUNTRIES);
  const [activeCountryCodes, setActiveCountryCodes] = useState<string[]>(FALLBACK_SUPPORTED_COUNTRIES.map((country) => country.code));
  const [mapsKeyError, setMapsKeyError] = useState<string | null>(null);
  const [mapsKeyLoading, setMapsKeyLoading] = useState(false);
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingDeep, setLoadingDeep] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [copiedShareLink, setCopiedShareLink] = useState(false);
  const [progressStatus, setProgressStatus] = useState<'idle' | 'running' | 'complete'>('idle');
  const [progressIndex, setProgressIndex] = useState<number | null>(null);
  const [listActionTarget, setListActionTarget] = useState<string | null>(null);
  const [deepProgress, setDeepProgress] = useState<number>(0);
  const deepProgressTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (completionTimeoutRef.current) {
        window.clearTimeout(completionTimeoutRef.current);
      }
      if (deepProgressTimerRef.current) {
        window.clearInterval(deepProgressTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (deepProgressTimerRef.current) {
      window.clearInterval(deepProgressTimerRef.current);
      deepProgressTimerRef.current = null;
    }

    if (deepStatus === 'running') {
      // Start at a small value so the fill is visible immediately.
      setDeepProgress((prev) => (prev > 5 ? prev : 8));
      deepProgressTimerRef.current = window.setInterval(() => {
        setDeepProgress((prev) => {
          const jitter = 3 + Math.random() * 7; // 3–10 %
          const next = prev + jitter;
          // Cap at ~92% so it doesn’t look uniform or finish before backend does.
          return Math.min(next, 92);
        });
      }, 3200 + Math.random() * 2800); // 3.2–6s cadence to look organic
    } else if (deepStatus === 'done') {
      setDeepProgress(100);
    } else {
      // idle or error
      setDeepProgress(0);
    }

    return () => {
      if (deepProgressTimerRef.current) {
        window.clearInterval(deepProgressTimerRef.current);
        deepProgressTimerRef.current = null;
      }
    };
  }, [deepStatus]);

  useEffect(() => {
    if (progressStatus !== 'running' || progressIndex === null) {
      return;
    }
    if (progressIndex >= GENERATION_STEPS.length - 1) {
      return;
    }
    const timer = window.setTimeout(() => {
      setProgressIndex((prev) => (prev === null ? prev : Math.min(prev + 1, GENERATION_STEPS.length - 1)));
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [progressStatus, progressIndex]);

  const startProgress = useCallback(() => {
    if (completionTimeoutRef.current) {
      window.clearTimeout(completionTimeoutRef.current);
      completionTimeoutRef.current = null;
    }
    setProgressStatus('running');
    setProgressIndex(0);
  }, []);

  const resetProgress = useCallback(() => {
    if (completionTimeoutRef.current) {
      window.clearTimeout(completionTimeoutRef.current);
      completionTimeoutRef.current = null;
    }
    setProgressStatus('idle');
    setProgressIndex(null);
  }, []);

  const completeProgress = useCallback(() => {
    setProgressStatus('complete');
    setProgressIndex(GENERATION_STEPS.length - 1);
    if (completionTimeoutRef.current) {
      window.clearTimeout(completionTimeoutRef.current);
    }
    completionTimeoutRef.current = window.setTimeout(() => {
      setProgressStatus('idle');
      setProgressIndex(null);
      completionTimeoutRef.current = null;
    }, 1800);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setMapsKeyLoading(!INITIAL_MAPS_KEY);
    setMapsKeyError(null);
    const fetchConfig = async () => {
      try {
        const config = await reportsApi.fetchMapsConfig();
        if (!cancelled) {
          const active = config.active_country_codes?.length
            ? config.active_country_codes.map((code) => code.toUpperCase())
            : FALLBACK_SUPPORTED_COUNTRIES.map((country) => country.code);
          const countries = config.supported_countries?.length
            ? config.supported_countries
            : FALLBACK_SUPPORTED_COUNTRIES;
          setActiveCountryCodes(active);
          setSupportedCountries(countries.filter((country) => country.active || active.includes(country.code)));
          const key = INITIAL_MAPS_KEY ?? config.maps_api_key;
          if (key) {
            setMapsApiKey(key);
          } else {
            setMapsKeyError('No Google Maps API key is configured. Please contact an admin.');
          }
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setMapsKeyError('Unable to fetch Google Maps API key.');
        }
      }
    };
    void fetchConfig().finally(() => {
      if (!cancelled) {
        setMapsKeyLoading(false);
      }
    });
    return () => {
      cancelled = true;
      setMapsKeyLoading(false);
    };
  }, []);

  useEffect(() => {
    if (manualMode || !mapsApiKey) {
      return;
    }
    setLoadingAutocomplete(true);
    let cancelled = false;

    loadGooglePlaces(mapsApiKey)
      .then((google) => {
        if (cancelled || !google || !inputRef.current) {
          return;
        }
        const AutocompleteConstructor = google.maps.places.Autocomplete;
        const countryRestrictions = activeCountryCodes.map((code) => code.toLowerCase());
        const activeCountrySet = new Set(activeCountryCodes.map((code) => code.toUpperCase()));
        const autocompleteOptions: ConstructorParameters<typeof AutocompleteConstructor>[1] = {
          fields: ['address_components', 'geometry', 'formatted_address', 'name', 'place_id', 'types'],
        };
        if (countryRestrictions.length > 0 && countryRestrictions.length <= 5) {
          autocompleteOptions.componentRestrictions = { country: countryRestrictions };
        }
        const autocomplete = new AutocompleteConstructor(inputRef.current, autocompleteOptions);
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place?.geometry?.location) {
            setError('Could not read coordinates for that place. Please try a different entry.');
            return;
          }
          const countryComponent = place.address_components?.find((component) => component.types?.includes('country'));
          const countryCode = getComponentShortValue(place.address_components, 'country')?.toUpperCase();
          if (countryCode && !activeCountrySet.has(countryCode)) {
            setError('gridfield currently analyses EU-27, the United Kingdom, Norway, and Switzerland. Please pick a supported site or use manual coordinates with country selection.');
            setSelectedLocation(null);
            setSearchValue('');
            return;
          }
          const latitude = place.geometry.location.lat();
          const longitude = place.geometry.location.lng();
          const components = place.address_components ?? [];
          const fallbackLabel = place.formatted_address ?? place.name ?? 'Custom location';
          const displayLabel = buildDisplayLabel(components, fallbackLabel);
          const rawName = (place.name ?? place.formatted_address ?? displayLabel)?.trim();
          const formattedAddress = normaliseFormattedAddress(place.formatted_address, displayLabel, components);
          setSelectedLocation({
            name: rawName || displayLabel,
            formatted_address: formattedAddress,
            latitude,
            longitude,
            place_id: place.place_id ?? undefined,
            country_code: countryCode,
            country: countryComponent?.long_name,
            address_components: components
          });
          setSearchValue(rawName || displayLabel);
          setError(null);
        });
        autocompleteRef.current = autocomplete;
      })
      .catch((error: unknown) => {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError('Unable to load map autocomplete.');
        }
        autocompleteRef.current = null;
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingAutocomplete(false);
        }
      });

    return () => {
      cancelled = true;
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [activeCountryCodes, manualMode, mapsApiKey]);

  const loadReport = useCallback(async (reportId: string) => {
    setLoadingReport(true);
    setDeepStatus('idle');
    setDeepHtml(null);
    setDeepError(null);
    setHistoryError(null);
    try {
      const detail = await reportsApi.fetchReport(reportId);
      setCurrentReport(detail);
      setSelectedReportId(reportId);
      return true;
    } catch (error: unknown) {
      console.error(error);
      setHistoryError('Unable to load that report. Please try again.');
      return false;
    } finally {
      setLoadingReport(false);
    }
  }, []);

  const fetchHistoryPage = useCallback(
    async (
      params: { limit: number; before?: string },
      attempt = 1
    ): Promise<{ results: SavedReport[]; next_cursor: string | null; has_more: boolean; total_count: number }> => {
      try {
        return await reportsApi.fetchHistory(params);
      } catch (error) {
        if (attempt < HISTORY_RETRY_ATTEMPTS) {
          await sleep(HISTORY_RETRY_DELAY_MS * attempt);
          return fetchHistoryPage(params, attempt + 1);
        }
        throw error;
      }
    },
    []
  );

  const loadHistory = useCallback(
    async (options?: { append?: boolean }) => {
      if (options?.append) {
        setLoadingMoreHistory(true);
      } else {
        setLoadingHistory(true);
        setHistoryError(null);
      }
      const params: { limit: number; before?: string } = { limit: HISTORY_PAGE_SIZE };
      if (options?.append && historyCursor) {
        params.before = historyCursor;
      } else if (!options?.append) {
        setHistoryCursor(null);
        setHasMoreHistory(false);
      }
      try {
        const history = await fetchHistoryPage(params);
        setTotalReportsCount(history.total_count);
        setHistoryCursor(history.has_more ? history.next_cursor ?? null : null);
        setHasMoreHistory(history.has_more);
        setReports((prev) => {
          const next = options?.append ? [...prev, ...history.results] : history.results;
          return dedupeReports(next);
        });
        return history.results;
      } catch (error: unknown) {
        console.error('Failed to load history', error);
        setHistoryError('Unable to fetch saved reports.');
        return [];
      } finally {
        if (options?.append) {
          setLoadingMoreHistory(false);
        } else {
          setLoadingHistory(false);
        }
      }
    },
    [fetchHistoryPage, historyCursor]
  );

  const loadReportWithRetry = useCallback(async (reportId: string): Promise<boolean> => {
    for (let attempt = 0; attempt < REPORT_FETCH_ATTEMPTS; attempt += 1) {
      const loaded = await loadReport(reportId);
      if (loaded) {
        return true;
      }
      await sleep(REPORT_FETCH_DELAY_MS * (attempt + 1));
    }
    return false;
  }, [loadReport]);

  const refreshDeepStatus = useCallback(async (reportId: string) => {
    try {
      const latest = await reportsApi.fetchDeepResearch(reportId);
      if (latest.status === 'succeeded') {
        setDeepHtml(latest.html_report ?? null);
        setDeepStatus('done');
        setDeepError(null);
      } else if (latest.status === 'failed') {
        setDeepError(latest.error_message ?? 'Deep Research fehlgeschlagen.');
        setDeepStatus('error');
        setDeepHtml(null);
      } else if (latest.status === 'running' || latest.status === 'queued') {
        setDeepStatus('running');
        setLoadingDeep(true);
        setDeepHtml(null);
        setDeepError(null);
      } else {
        setDeepStatus('idle');
        setDeepHtml(null);
      }
    } catch (pollErr) {
      // If no record exists yet, stay idle; otherwise surface the error.
      console.error(pollErr);
    }
  }, []);

  const triggerDeepResearch = useCallback(async () => {
    if (!currentReport?.id) return;
    try {
      setDeepStatus('running');
      setDeepHtml(null);
      setDeepError(null);
      const res = await reportsApi.triggerDeepResearch(currentReport.id);
      if (res.status === 'failed') {
        setDeepError(res.error_message ?? 'Deep Research fehlgeschlagen.');
        setDeepStatus('error');
      }
    } catch (err) {
      console.error(err);
      setDeepError('Deep Research fehlgeschlagen.');
      setDeepStatus('error');
    }
    setLoadingDeep(false);
  }, [currentReport]);

  const openDeepResearchReport = useCallback(() => {
    if (!currentReport?.id) return;
    const url = `https://api.gridfield.co/api/reports/public/${currentReport.id}/deep-research/latest/`;
    window.open(url, '_blank', 'noopener');
  }, [currentReport?.id]);

  const loadHistoryRef = useRef(loadHistory);
  const loadReportRef = useRef(loadReport);

  useEffect(() => {
    loadHistoryRef.current = loadHistory;
  }, [loadHistory]);

  useEffect(() => {
    loadReportRef.current = loadReport;
  }, [loadReport]);

  // Refresh deep-research status when the report changes.
  useEffect(() => {
    setDeepStatus('idle');
    setDeepError(null);
    setDeepHtml(null);
    setLoadingDeep(false);
    if (currentReport?.id) {
      setLoadingDeep(true);
      void refreshDeepStatus(currentReport.id).finally(() => setLoadingDeep(false));
    }
  }, [currentReport?.id, refreshDeepStatus]);

  // Poll deep-research status while running.
  useEffect(() => {
    if (deepStatus !== 'running' || !currentReport?.id) {
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      await refreshDeepStatus(currentReport.id);
    };
    const interval = window.setInterval(poll, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [deepStatus, currentReport?.id, refreshDeepStatus]);

  useEffect(() => {
    void (async () => {
      const history = await loadHistoryRef.current();
      if (history.length > 0) {
        await loadReportRef.current(history[0].id);
      }
    })();
  }, []);

  const handleGenerate = async () => {
    setError(null);
    let payload: AnalyzeLocationPayload | null = null;

    if (manualMode) {
      const lat = Number.parseFloat(manualLatitude);
      const lng = Number.parseFloat(manualLongitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        setError('Enter valid decimal coordinates before generating.');
        return;
      }
      if (!manualCountry?.code) {
        setError('Select the parcel country before generating.');
        return;
      }
      const label = manualLabel.trim();
      payload = {
        latitude: lat,
        longitude: lng,
        name: label || 'Custom location',
        formatted_address: label || `${lat.toFixed(5)}, ${lng.toFixed(5)} (${manualCountry.code})`,
        country_code: manualCountry.code,
        country: manualCountry.name,
        address_components: [
          {
            long_name: manualCountry.name,
            short_name: manualCountry.code,
            types: ['country', 'political']
          }
        ]
      };
    } else if (selectedLocation) {
      const { address_components: components, ...rest } = selectedLocation;
      payload = {
        ...rest,
        address_components: components?.map((component) => ({
          long_name: component.long_name,
          short_name: component.short_name,
          types: component.types
        }))
      };
    } else {
      setError('Choose a location first. Start typing and pick one of the suggestions.');
      return;
    }

    setGenerating(true);
    startProgress();
    try {
      const response: AnalyzeLocationResponse = await reportsApi.analyzeLocation(payload);
      // Optimistically add the new report so it appears immediately in the list.
      const optimistic: SavedReport = {
        id: response.report_id,
        name: payload.name ?? payload.formatted_address ?? 'Custom location',
        latitude: payload.latitude,
        longitude: payload.longitude,
        formatted_address: payload.formatted_address,
        created_at: new Date().toISOString(),
        public_url: response.report_url,
        hazard_report_url: response.hazard_report_url
      };
      setReports((prev) => dedupeReports([optimistic, ...prev]));
      setSelectedReportId(response.report_id);

      await loadHistory();
      const loaded = await loadReportWithRetry(response.report_id);
      if (loaded) {
        completeProgress();
      } else {
        resetProgress();
      }
    } catch (error: unknown) {
      console.error(error);
      setError('Could not generate the report right now. Please try again in a moment.');
      resetProgress();
    } finally {
      setGenerating(false);
    }
  };

  const buildDownloadName = (report: SavedReportDetail) => {
    const postal = report.analysis?.location?.postal_code?.trim();
    const nameParts = [report.name?.trim(), postal].filter(Boolean).join(' - ');
    const base = nameParts || report.name?.trim() || 'location';
    const cleaned = base.replace(/[/\\?%*:|"<>]/g, '').trim() || 'location';
    return `gridfield location report - ${cleaned}.html`;
  };

  const handleDownloadHtml = () => {
    if (!currentReport) return;
    const blob = new Blob([currentReport.html_report], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = buildDownloadName(currentReport);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const handleOpenInNewTab = () => {
    if (currentReport?.public_url) {
      window.open(currentReport.public_url, '_blank', 'noopener');
      return;
    }
    if (!currentReport) return;
    const blob = new Blob([currentReport.html_report], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const handleCopyShareLink = async () => {
    if (!currentReport?.public_url || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(currentReport.public_url);
      setCopiedShareLink(true);
      setTimeout(() => setCopiedShareLink(false), 2000);
    } catch (err) {
      console.error('Unable to copy share link', err);
    }
  };

  const handleRenameReport = async (reportId: string) => {
    const target = reports.find((report) => report.id === reportId);
    if (!target) return;
    const nextName = window.prompt('Rename report', target.name);
    if (!nextName) {
      return;
    }
    const trimmed = nextName.trim();
    if (!trimmed || trimmed === target.name) {
      return;
    }
    try {
      setListActionTarget(reportId);
      const updated = await reportsApi.renameReport(reportId, { name: trimmed });
      setReports((prev) =>
        prev.map((report) => (report.id === reportId ? { ...report, name: updated.name } : report))
      );
      if (currentReport?.id === reportId) {
        setCurrentReport((prev) => (prev ? { ...prev, name: updated.name } : prev));
      }
    } catch (error) {
      console.error(error);
      setHistoryError('Unable to rename that report right now.');
    } finally {
      setListActionTarget(null);
    }
  };

  const handleArchiveReport = async (reportId: string) => {
    try {
      setListActionTarget(reportId);
      await reportsApi.archiveReport(reportId);
      const refreshed = await loadHistory();
      if (currentReport?.id === reportId) {
        if (refreshed.length > 0) {
          await loadReport(refreshed[0].id);
        } else {
          setCurrentReport(null);
          setSelectedReportId(null);
        }
      }
    } catch (error) {
      console.error(error);
      setHistoryError('Unable to remove that report right now.');
    } finally {
      setListActionTarget(null);
    }
  };

  const selectedReportCreatedAt = useMemo(() => {
    if (!currentReport?.created_at) return null;
    const date = new Date(currentReport.created_at);
    return Number.isNaN(date.getTime()) ? currentReport.created_at : date.toLocaleString();
  }, [currentReport]);
  const activeCountries = useMemo(() => {
    const activeSet = new Set(activeCountryCodes);
    return supportedCountries.filter((country) => country.active || activeSet.has(country.code));
  }, [activeCountryCodes, supportedCountries]);
  const manualCountry = activeCountries.find((country) => country.code === manualCountryCode) ?? activeCountries[0];
  useEffect(() => {
    if (manualCountry) {
      setManualCountryCode((current) => (activeCountries.some((country) => country.code === current) ? current : manualCountry.code));
    }
  }, [activeCountries, manualCountry]);
  const normalizeLabel = (value?: string | null) => {
    if (!value) return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  };
const toSingleLineTitle = (value: string | null) => {
  if (!value) return 'Custom location';
  const trimmed = value.trim();
  if (trimmed.length <= 60) return trimmed;
  const parts = trimmed.split(',').map((part) => part.trim()).filter(Boolean);
  const dropIfNumericOrCountry = (segment: string) =>
    /\d/.test(segment) || /germany/i.test(segment) || segment.length > 18;
  const working = [...parts];
  while (working.length > 1 && working.join(', ').length > 70) {
    const last = working[working.length - 1];
    if (dropIfNumericOrCountry(last)) {
      working.pop();
    } else {
      working.pop();
    }
  }
  const joined = working.join(', ');
  if (joined.length > 70) {
    return `${joined.slice(0, 67)}…`;
  }
  return joined;
};
  const headerTitle =
    normalizeLabel(currentReport?.name) ??
    normalizeLabel(currentReport?.formatted_address) ??
    normalizeLabel(currentReport?.analysis?.location?.formatted_address) ??
    'Custom location';
  const headerDisplayTitle = toSingleLineTitle(headerTitle);

  const canGenerate = manualMode
    ? manualLatitude.trim().length > 0 && manualLongitude.trim().length > 0 && Boolean(manualCountry?.code)
    : !!selectedLocation;
  const stepsCount = GENERATION_STEPS.length;
  const [progressMeter, setProgressMeter] = useState(0);
  useEffect(() => {
    if (progressStatus === 'idle') {
      setProgressMeter(0);
    } else if (progressStatus === 'complete') {
      setProgressMeter(1);
    }
  }, [progressStatus]);
  useEffect(() => {
    if (progressStatus !== 'running') {
      return;
    }
    const stageStart = progressIndex !== null ? progressIndex / stepsCount : 0;
    setProgressMeter((prev) => Math.max(prev, stageStart));
  }, [progressIndex, progressStatus, stepsCount]);
  useEffect(() => {
    if (progressStatus !== 'running') {
      return;
    }
    let raf: number;
    const animate = () => {
      setProgressMeter((prev) => {
        const stage = progressIndex ?? 0;
        const stageStart = stage / stepsCount;
        const stageEnd = Math.min(1, (stage + 1) / stepsCount);
        const cap = stageEnd - 0.05;
        const baseline = Math.max(stageStart, prev);
        if (baseline >= cap) {
          return baseline;
        }
        return Math.min(cap, baseline + 0.0035);
      });
      raf = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(raf);
  }, [progressIndex, progressStatus, stepsCount]);
  const progressLabel =
    progressStatus === 'complete'
      ? 'Report ready'
      : progressStatus === 'running' && progressIndex !== null
        ? GENERATION_STEPS[progressIndex]
        : 'Preparing…';
  const progressPercent =
    progressStatus === 'complete'
      ? 1
      : Math.max(progressMeter, progressStatus === 'running' ? 0.02 : 0);
  const savedReportsCount = totalReportsCount ?? reports.length;
  const savedCountLabel = hasMoreHistory
    ? `${savedReportsCount.toLocaleString()}+`
    : savedReportsCount.toLocaleString();
  const progressDisplay = Math.min(100, Math.max(0, Math.round(progressPercent * 100)));
  return (
    <AppShell title="Location intelligence reports">
      <section className="w-full rounded-[28px] border border-black/5 bg-white/80 px-5 py-6 shadow-[0_20px_60px_rgba(0,0,0,0.04)] lg:px-7">
        {!manualMode ? (
          <div className="mx-auto w-full max-w-4xl space-y-4">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={searchValue}
                onChange={(event) => {
                  setSearchValue(event.target.value);
                  setSelectedLocation(null);
                }}
                placeholder={loadingAutocomplete ? 'Loading…' : 'Start typing an address, coordinate or site name'}
                className="w-full rounded-full border border-black/10 bg-white/90 px-5 py-2.5 text-sm font-light text-[#1a1b23] placeholder-[#9b9cb1] shadow-[inset_0_1px_8px_rgba(0,0,0,0.04)] focus:border-black/30 focus:outline-none focus:ring-2 focus:ring-black/10 disabled:opacity-60"
                disabled={loadingAutocomplete || !mapsApiKey}
              />
            </div>
            {!mapsApiKey && (
              <p className="text-xs text-[#8a8b99]">
                {mapsKeyLoading && 'Loading Google Maps configuration…'}
                {!mapsKeyLoading && (mapsKeyError ?? 'Google Places autocomplete unavailable. Set GOOGLE_MAPS_API_KEY.')}
              </p>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={handleGenerate}
                disabled={!canGenerate || generating}
                className="flex-1 rounded-full bg-[#0f0f19] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-white shadow-[0_10px_22px_rgba(0,0,0,0.12)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {generating ? 'Generating…' : 'Generate report'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setManualMode(true);
                  setError(null);
                  setSelectedLocation(null);
                  setSearchValue('');
                }}
                className="flex-1 rounded-full border border-black/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#5a5b6c] transition hover:border-black/30 hover:text-[#0f0f19]"
              >
                Enter coordinates
              </button>
            </div>
          </div>
        ) : (
          <div className="mx-auto mt-10 w-full max-w-4xl space-y-6 text-left">
            <div className="grid gap-4 md:grid-cols-4">
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8a8b99]">
                Site label
                <input
                  type="text"
                  value={manualLabel}
                  onChange={(event) => setManualLabel(event.target.value)}
                  placeholder="Custom name"
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0f0f19] placeholder-[#b0b0bf] focus:border-black/30 focus:outline-none focus:ring-2 focus:ring-black/15"
                />
              </label>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8a8b99]">
                Country
                <select
                  value={manualCountryCode}
                  onChange={(event) => setManualCountryCode(event.target.value)}
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0f0f19] focus:border-black/30 focus:outline-none focus:ring-2 focus:ring-black/15"
                >
                  {activeCountries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8a8b99]">
                Latitude
                <input
                  type="text"
                  inputMode="decimal"
                  value={manualLatitude}
                  onChange={(event) => setManualLatitude(event.target.value)}
                  placeholder="48.1372"
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0f0f19] placeholder-[#b0b0bf] focus:border-black/30 focus:outline-none focus:ring-2 focus:ring-black/15"
                />
              </label>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8a8b99]">
                Longitude
                <input
                  type="text"
                  inputMode="decimal"
                  value={manualLongitude}
                  onChange={(event) => setManualLongitude(event.target.value)}
                  placeholder="11.5761"
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0f0f19] placeholder-[#b0b0bf] focus:border-black/30 focus:outline-none focus:ring-2 focus:ring-black/15"
                />
              </label>
            </div>
            {error && <p className="text-sm text-red-300">{error}</p>}
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleGenerate}
                disabled={!canGenerate || generating}
                className="flex-1 rounded-full bg-[#0f0f19] px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.35em] text-white shadow-[0_16px_35px_rgba(0,0,0,0.17)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {generating ? 'Generating…' : 'Create new dossier'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setManualMode(false);
                  setManualLatitude('');
                  setManualLongitude('');
                  setManualLabel('');
                  setManualCountryCode(activeCountries[0]?.code ?? 'DE');
                }}
                className="flex-1 rounded-full border border-black/15 px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.35em] text-[#5a5b6c] transition hover:border-black/30 hover:text-[#0f0f19]"
              >
                Use search
              </button>
            </div>
          </div>
        )}
        {progressStatus !== 'idle' && (
          <div className="mx-auto mt-6 w-full max-w-4xl rounded-[28px] border border-black/5 bg-white/80 p-4 text-left shadow-[0_20px_50px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-between text-[0.65rem] font-semibold uppercase tracking-[0.35em] text-[#8a8b99]">
              <span>{progressLabel}</span>
              <span>{progressDisplay}%</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/5">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#0f0f19] via-[#1b1c2d] to-[#2f3142] transition-all duration-500"
                style={{ width: `${Math.min(100, progressPercent * 100).toFixed(1)}%` }}
              />
            </div>
          </div>
        )}
      </section>

      <div className="mt-6 flex flex-col gap-6 lg:grid lg:grid-cols-[360px_minmax(0,1fr)] lg:items-stretch">
        <aside className="order-2 flex flex-col rounded-[28px] border border-black/5 bg-white/75 p-5 shadow-[0_20px_55px_rgba(0,0,0,0.04)] lg:order-1">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8a8b99]">Saved reports</h3>
            <span className="rounded-full border border-black/15 px-2 py-0.5 text-[10px] font-semibold text-[#0f0f19]">
              {savedCountLabel}
            </span>
          </div>
          {historyError && <p className="mt-3 text-xs text-red-300">{historyError}</p>}
          <div className="mt-3 flex-1 overflow-y-auto pr-1">
            <ul className="space-y-3 text-left text-sm text-[#0f0f19]">
              {loadingHistory &&
                Array.from({ length: 4 }).map((_, index) => (
                  <li
                    key={`skeleton-${String(index)}`}
                    className="animate-pulse rounded-xl border border-black/10 bg-[#f5f5f5] px-4 py-3 text-xs text-[#8a8b99]"
                  >
                    Loading saved reports…
                  </li>
                ))}
              {!loadingHistory && reports.length === 0 && !historyError && (
                <li className="text-xs text-[#8a8b99]">No reports yet. Generate one above and it will appear here.</li>
              )}
              {!loadingHistory &&
                reports.map((item) => {
                  const trimmedName = item.name?.trim();
                  const trimmedAddress = item.formatted_address?.trim();
                  const hasAddress = Boolean(trimmedAddress);
                  const hasDistinctAddress =
                    hasAddress &&
                    (!trimmedName || trimmedAddress?.toLowerCase() !== trimmedName?.toLowerCase());
                  const coordinatesLabel = `Lat ${item.latitude.toFixed(3)}° · Lon ${item.longitude.toFixed(3)}°`;
                  const secondaryLine = hasDistinctAddress ? trimmedAddress : coordinatesLabel;
                  return (
                    <li key={item.id} className="group relative">
                      <button
                        type="button"
                        onClick={() => void loadReport(item.id)}
                        className={`w-full rounded-xl border px-4 py-2 text-left transition ${
                          selectedReportId === item.id
                          ? 'border-black text-[#0f0f19]'
                          : 'border-black/10 bg-[#f9f9f9] hover:border-black/30 hover:bg-white'
                      }`}
                      >
                      <p className="truncate text-sm font-semibold" title={item.name}>
                        {item.name}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-[#8a8b99]" title={secondaryLine}>
                        {secondaryLine}
                      </p>
                    </button>
                    <div className="pointer-events-none absolute right-3 top-2 flex gap-2 opacity-0 transition group-hover:pointer-events-auto group-hover:opacity-100">
                      <button
                        type="button"
                        aria-label="Rename report"
                        className="pointer-events-auto rounded-full border border-black/10 bg-white px-2 py-1 text-[10px] font-semibold text-[#5a5b6c] hover:border-black/30 hover:text-[#0f0f19] disabled:opacity-50"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleRenameReport(item.id);
                        }}
                        disabled={listActionTarget === item.id}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        aria-label="Remove report"
                        className="pointer-events-auto rounded-full border border-black/10 bg-white px-2 py-1 text-[10px] font-semibold text-[#5a5b6c] hover:border-rose-400 hover:text-rose-500 disabled:opacity-50"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleArchiveReport(item.id);
                        }}
                        disabled={listActionTarget === item.id}
                      >
                        ×
                      </button>
                    </div>
                  </li>
                  );
                })}
            </ul>
          </div>
          {hasMoreHistory && (
            <button
              type="button"
              onClick={() => void loadHistory({ append: true })}
              className="mt-4 w-full rounded-full border border-black/10 px-4 py-2 text-xs font-semibold text-[#5a5b6c] hover:border-black/30 hover:text-[#0f0f19] disabled:opacity-50"
              disabled={loadingMoreHistory}
            >
              {loadingMoreHistory ? 'Loading…' : 'Load older reports'}
            </button>
          )}
        </aside>

        <section className="order-1 rounded-[40px] border border-black/5 bg-white/90 p-8 shadow-[0_30px_80px_rgba(0,0,0,0.05)] lg:order-2">
          {loadingReport && (
            <p className="text-sm text-[#8a8b99]">Loading report…</p>
          )}
          {!loadingReport && !currentReport && (
            <p className="text-sm text-[#8a8b99]">Select a report from the left or generate a new one.</p>
          )}
          {!loadingReport && currentReport && (
            <div className="flex h-full flex-col gap-8">
              <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-[#5a5b6c]">
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <button
                    type="button"
                    onClick={handleDownloadHtml}
                    className="rounded-full border border-black/10 px-4 py-2 font-semibold text-[#0f0f19] hover:border-black/30"
                  >
                    Download HTML
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenInNewTab}
                    className="rounded-full border border-black/10 px-4 py-2 font-semibold text-[#0f0f19] hover:border-black/30"
                  >
                    Open full report
                  </button>
                </div>
                {currentReport.public_url && (
                  <div className="flex items-center gap-2 text-left text-xs text-[#8a8b99]">
                    <p>Shareable link</p>
                    <button
                      type="button"
                      onClick={handleCopyShareLink}
                      className="rounded-full border border-black/10 px-3 py-1 font-semibold text-[#0f0f19] hover:border-black/30"
                    >
                      {copiedShareLink ? 'Copied!' : 'Copy link'}
                    </button>
                  </div>
                )}
              </div>
              <header className="rounded-2xl border border-black/10 bg-white p-6 shadow-[0_15px_45px_rgba(0,0,0,0.08)]">
                <div className="flex flex-nowrap items-start justify-between gap-4 text-left">
                  <div className="min-w-0 flex-1">
                    <h3
                      className="max-w-[min(100%,960px)] truncate whitespace-nowrap text-2xl font-semibold leading-tight text-[#0f0f19]"
                      title={headerTitle ?? undefined}
                    >
                      {headerDisplayTitle}
                    </h3>
                  </div>
                  <div className="flex-none text-left text-xs text-[#8a8b99] md:text-right">
                    <p className="font-semibold text-[#0f0f19]">
                      Lat {currentReport.latitude.toFixed(4)}° · Lon {currentReport.longitude.toFixed(4)}°
                    </p>
                    <p>Generated {selectedReportCreatedAt ?? 'just now'}</p>
                    <div className="mt-2 flex flex-wrap gap-2 justify-end">
                      {deepStatus === 'done' && deepHtml ? (
                        <button
                          type="button"
                          onClick={openDeepResearchReport}
                          className="rounded-full border border-emerald-400/70 px-3 py-1 text-[11px] font-semibold text-emerald-700 hover:border-emerald-600"
                        >
                          Open research report
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={triggerDeepResearch}
                            className="relative overflow-hidden rounded-full border border-black/15 px-3 py-1 font-semibold text-[#0f0f19] hover:border-black/30 text-[11px]"
                            disabled={deepStatus === 'running' || loadingDeep}
                            style={
                              deepStatus === 'running'
                                ? {
                                    background: `linear-gradient(90deg, rgba(16,185,129,0.18) ${deepProgress.toFixed(1)}%, #f9fafb ${deepProgress.toFixed(1)}%)`,
                                    transition: 'background 1.2s ease',
                                  }
                                : undefined
                            }
                          >
                            {deepStatus === 'running' ? 'Research running…' : 'Background research'}
                          </button>
                          {deepStatus === 'error' && (
                            <span className="text-[11px] font-semibold text-rose-500">
                              {deepError ?? 'Deep research failed (see logs).'}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </header>

              <section>
                <h4 className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8a8b99]">Scores</h4>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  {SCORE_CONFIG.map(({ key, label }) => (
                    <ScoreBar
                      key={key}
                      label={label}
                      value={getScoreValue(currentReport.analysis.scores[key])}
                      tooltip={SCORE_DESCRIPTIONS[key] ?? SCORE_DESCRIPTIONS.overall}
                    />
                  ))}
                </div>
              </section>

              <section>
                <h4 className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8a8b99]">Highlights</h4>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  {(currentReport.analysis.summary_cards ?? []).map((card) => {
                    const radiusLabel = formatRadiusLabel(card);
                    const nearestDistance =
                      typeof card.nearest?.distance_km === 'number' ? card.nearest.distance_km.toFixed(1) : null;
                    const nearestLabel = card.nearest?.name
                      ? `${card.nearest.name}${nearestDistance ? ` · ${nearestDistance} km` : ''}`
                      : null;
                    return (
                      <div
                        key={card.label}
                        className="rounded-2xl border border-black/10 bg-[#f9f9f9] p-4 text-left shadow-[0_12px_30px_rgba(0,0,0,0.08)] transition hover:border-black/30 hover:bg-white"
                        title={nearestLabel ?? undefined}
                      >
                        <p className="text-3xl font-semibold text-[#0f0f19]">{card.count ?? 'n/a'}</p>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#8a8b99]">{card.label}</p>
                        <p className="mt-1 text-[11px] text-[#8a8b99]">
                          {radiusLabel ?? 'Nearest asset highlighted'}
                        </p>
                        {nearestLabel && <p className="mt-2 text-xs text-[#5a5b6c]">Nearest: {nearestLabel}</p>}
                      </div>
                    );
                  })}
                </div>
              </section>

              {SHOW_HAZARD_SECTION && currentReport.analysis.hazard && (
              <section className="pt-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8a8b99]">Hazard profile</h4>
                  {currentReport.hazard_report_url && (
                    <a
                      href={currentReport.hazard_report_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#0f0f19] hover:text-black"
                    >
                      View hazard report
                    </a>
                  )}
                </div>
                <div className="mt-3">
                  <HazardModule hazard={currentReport.analysis.hazard} />
                </div>
              </section>
              )}

            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
};

export default ReportsPage;
